import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { Subject, takeUntil } from "rxjs";
import { Store } from "@ngrx/store";
import * as fromRoot from "../../../../../../state/app.state";
import {
  groupStatusColors,
  roles,
  roleUrlMap, sortOrderMap
} from '../../../../../shared/constants/common.constants';
import { ActivatedRoute, Router } from "@angular/router";
import {
  drillDownUrls,
  profile,
  profileData,
  selectedUserProfile
} from "../../../../../shared/core/shared.selectors";
import { FetchGroupsListRequest } from "../../../core/admin-groups-list.actions";
import { groupsList } from "../../../core/admin-groups-list.selectors";
import { GroupsList } from "../../../../../../models/groups-list";
import {
  AdminGroupListService
} from "../../../services/admin-group-list.service";
import { CommonService } from '../../../../../shared/services/common.service';
import { MatSort, Sort } from "@angular/material/sort";
import { SetDrillDownUrls } from "../../../../../shared/core/shared.actions";
import {
  DeleteGroupRequest,
  SetDuplicateGroupRequest
} from "../../../../group-details/core/group-details.actions";
import { MatTable, MatTableDataSource } from "@angular/material/table";

@Component({
  selector: 'app-group-information-table',
  templateUrl: './group-information-table.component.html',
  styleUrls: ['./group-information-table.component.css']
})
export class GroupInformationTableComponent implements OnDestroy {
  @ViewChild(MatTable) table: MatTable<any>;
  @ViewChild(MatSort) sort: MatSort;

  loading: boolean = false;
  tableData: any[];
  dataSource: any = new MatTableDataSource();
  role: number;
  roles = roles;
  userRole: number;
  sortColumn: any = 'startDate';
  sortOrder: any= -1;

  isFirstPage = true;
  selectedUserRole: number | null;
  groupListData: GroupsList;
  onApplyGroupListFilter: any;
  typeMap = {
    1: 'Participant',
    2: 'Adult',
  };

  displayedColumns = [
    'name',
    'coaches',
    'date',
    'type',
    'status',
    'participantcount',
    'currentSession',
    'upcomingSession',
    'totalAttendance',
    'actionMenu',
  ];
  actionMenu = [
    {
      displayName: 'Duplicate',
      icon: 'edit',
      highLightColor: '',
      handleClick: (data) => this.handleDuplicateOption(data)
    },
    {
      displayName: 'Delete',
      icon: 'delete',
      highLightColor: '',
      handleClick: (data) => this.handleDeleteOption(data),
      status: [1]
    }
  ]
  clientId: any;
  user;
  selectedUser;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private router: Router,
    private cdref: ChangeDetectorRef,
    private route: ActivatedRoute,
    private adminGroupListService: AdminGroupListService,
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
  ) {

    if (this.route.snapshot.paramMap.get("clientId")) {
      this.clientId = this.route.snapshot.paramMap.get("clientId");
      this.fetchGroupsList();
    }
    else if (this.route.snapshot.paramMap.get("clientAdminId")) {
      this.store.select(profile)
        .pipe(takeUntil(this.onDestroy))
        .subscribe(data => {
          this.user = data;
        });

      this.store.select(selectedUserProfile)
        .pipe(takeUntil(this.onDestroy))
        .subscribe(data => {
          if (data.profile) {
            this.selectedUser = data?.profile;
            let clientId = this.user.clientId;

            if (this.user?.role && this.selectedUser?.role) {
              clientId = this.selectedUser.clientId;
            }
            this.clientId = clientId
            this.fetchGroupsList();
          }
        });
    }
    else {
      this.fetchGroupsList();
    }


    this.store.select(groupsList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.loading = data.loading;
        this.groupListData = data;
        if (!data.loading) {
          this.dataSource.data = [...data.list];

          if (data.hasNext) {
            setTimeout(() => {
              if (this.sortColumn && data.pageNumber === 1) {
                this.commonService.scrollColumnInView(this.sortColumn);
              }
            }, 0)

            setTimeout(() => {
              // check window height for infinite scroll
              this.commonService.checkInfiniteScrollHeightAndCallNext('table-wrapper', () => {
                this.onScroll();
              });
            }, 0)
          }
        }
      });

    this.store.select(profileData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.role = data.profile?.role;
      })

    // listen for filter changes from the filter component
    this.adminGroupListService.onGroupFilterChange.pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.onApplyGroupListFilter = data;
        this.isFirstPage = true;
        this.fetchGroupsList();
      })
  }

  fetchGroupsList(pageNumber = 1, limit = 10) {
    this.store.dispatch(FetchGroupsListRequest({
      payload: {
        ...this.onApplyGroupListFilter,
        pageNumber,
        limit,
        ...(this.sortOrder ? { sortOrder: this.sortOrder } : {}),
        ...(this.sortOrder ? { sortColumn: this.sortColumn } : {}),
        ...(this.clientId ? { clientId: this.clientId } : {})
      }
    }));
  }

  onScroll() {
    if (this.groupListData?.hasNext && !this.groupListData?.loading) {
      this.fetchGroupsList(this.groupListData.pageNumber + 1, this.groupListData.limit);
      this.isFirstPage = false;
    }
  }

  groupNameClick(data) {
    if (data?.id) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({ payload: updatedDrillDownUrls }));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];
      this.router.navigate([`/${urlPrefix}/group/${data.id}/dashboard`]);

    }
  }

  coachNameClick(coach: any) {
    if (coach?.id) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({ payload: updatedDrillDownUrls }));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];
      this.router.navigate([`/${urlPrefix}/coach/${coach.id}/groups`]);
    }
  }

  sessionNameClick(item: any) {
    if (item?.id) {
      this.store.select(drillDownUrls).subscribe(data => {
        const updatedDrillDownUrls = [...data, this.router.url];
        if ([...data].pop() !== this.router.url) {
          this.store.dispatch(SetDrillDownUrls({ payload: updatedDrillDownUrls }));
        }
      }).unsubscribe();

      const urlPrefix = this.router.url.split('/')[1];
      this.router.navigate([`/${urlPrefix}/group/${item.id}/sessions/${item.lastSessionId}/details`]);
    }
  }

  handleDuplicateOption(group) {
    const payload = {
      groupId: group.id,
    };
    this.store.dispatch(SetDuplicateGroupRequest({ payload }));
    this.router.navigate([`${roleUrlMap[this.role]}/group/duplicate`]);
  }

  handleDeleteOption(group) {
    const payload = {
      groupId: group.id,
      isActive: false,
      refresh: true,
      clientId: group.clientId
    };
    this.store.dispatch(DeleteGroupRequest({payload}));
  }

  sortData(sort: Sort) {
    this.isFirstPage = true;
    this.sortColumn = `${sort.active}`;
    this.sortOrder = sortOrderMap[sort.direction];

    this.fetchGroupsList();
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  protected readonly groupStatusColors = groupStatusColors;
}
